import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Safe JSON type compatible with Supabase JSON columns
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json }
  | Json[];

export interface GlobalSetting {
  id: string;
  setting_key: string;
  setting_value: Json;
  description?: string;
  category: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export const useGlobalSettings = (category?: string) => {
  return useQuery({
    queryKey: ['global_settings', category],
    queryFn: async (): Promise<GlobalSetting[]> => {
  if (!isSupabaseConfigured) return [];
      let query = supabase
        .from('global_settings')
        .select('*')
        .order('setting_key');
      
      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching global settings:', error);
        throw error;
      }
      
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useUpdateGlobalSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      settingKey, 
      settingValue, 
      category = 'general',
      description,
      isPublic = false 
    }: {
      settingKey: string;
  settingValue: Json;
      category?: string;
      description?: string;
      isPublic?: boolean;
    }) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      // Check if setting exists
      const { data: existingSetting } = await supabase
        .from('global_settings')
        .select('id')
        .eq('setting_key', settingKey)
        .single();

      if (existingSetting) {
        // Update existing setting
        const { data, error } = await supabase
          .from('global_settings')
          .update({
            setting_value: settingValue,
            category,
            description,
            is_public: isPublic,
            updated_at: new Date().toISOString()
          })
          .eq('setting_key', settingKey)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new setting
        const { data, error } = await supabase
          .from('global_settings')
          .insert({
            setting_key: settingKey,
            setting_value: settingValue,
            category,
            description,
            is_public: isPublic
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global_settings'] });
      toast.success('تم حفظ الإعداد بنجاح');
    },
    onError: (error: unknown) => {
      console.error('Error updating global setting:', error);
      toast.error('فشل في حفظ الإعداد');
    }
  });
};

export const useDeleteGlobalSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settingKey: string) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      const { error } = await supabase
        .from('global_settings')
        .delete()
        .eq('setting_key', settingKey);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global_settings'] });
      toast.success('تم حذف الإعداد بنجاح');
    },
    onError: (error: unknown) => {
      console.error('Error deleting global setting:', error);
      toast.error('فشل في حذف الإعداد');
    }
  });
};

// Helper hook to get a specific setting value
export const useGlobalSetting = <T = unknown>(settingKey: string, defaultValue?: T) => {
  const { data: settings } = useGlobalSettings();
  
  const setting = settings?.find(s => s.setting_key === settingKey);
  return (setting?.setting_value as unknown as T) ?? defaultValue;
};

// Helper hook to get settings by category
export const useSettingsByCategory = () => {
  const { data: settings, ...rest } = useGlobalSettings();
  
  const categorizedSettings = settings?.reduce((acc, setting) => {
    const category = setting.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {} as Record<string, GlobalSetting[]>) || {};

  return {
    data: categorizedSettings,
    ...rest
  };
};